// A Learn story (contracts/learn.md §1): its category, the puzzle types it teaches, the cover, the title and
// subtitle per language, the button, the lesson push, and 1–12 slides, with a phone preview of the viewer.
// Editors save, publish now or on a schedule (UTC), archive, and delete drafts; every role can read it.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  type AdminStory,
  CATEGORY_LABEL,
  CTA_ACTIONS,
  CTA_LABEL,
  type CtaAction,
  MAX_SLIDES,
  STATUS_TONE,
  STORY_CATEGORIES,
  STORY_LIMITS,
  type StoryCategory,
  type StoryContent,
  type StoryDraft,
  useArchiveStory,
  useCreateStory,
  useDeleteStory,
  usePublishStory,
  useStory,
  useUpdateStory,
} from "../../api/learn";
import { APP_LINK_LABEL, APP_LINKS, type AppLink, type ImageRef, type Locale } from "../../api/messaging";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, Dialog, ErrorState, Field, InlineError, Loading, PageHeader, Select, TextInput } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import { COVER_IMAGE, type EditSlide, slideFromApi, slideToApi, storyProblems } from "../../lib/learn";
import { fromUtcInput, toLocalized, toUtcInput } from "../../lib/messaging";
import { ContentEditor, type ContentState, type FieldSpec, ImageField, SwitchRow, textState } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import { ClaimsNote, PuzzleTypesField, SlidesEditor, type SlidesTranslation, StoryPreview } from "./components";
import l from "./learn.module.css";

const TEXT_FIELDS = ["title", "subtitle", "cta"] as const;
type TextKey = (typeof TEXT_FIELDS)[number];
const TEXT_SPECS: readonly FieldSpec<TextKey>[] = [
  { key: "title", label: "Title", limit: STORY_LIMITS.title, placeholder: "e.g. How to read a matrix", hint: "the push title" },
  { key: "subtitle", label: "Subtitle", limit: STORY_LIMITS.subtitle, rows: 2, placeholder: "e.g. Rows, columns and the one rule that ties them together.", hint: "the push text" },
  { key: "cta", label: "Button label", limit: STORY_LIMITS.cta, placeholder: "e.g. Practise matrices", hint: "on the last slide" },
];

export function StoryEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const story = useStory(isNew ? undefined : id);
  if (!isNew && story.isPending) {
    return (
      <Card>
        <Loading rows={8} />
      </Card>
    );
  }
  if (!isNew && story.isError) return <ErrorState error={story.error} onRetry={() => void story.refetch()} />;
  return <StoryEditor key={isNew ? "new" : id} story={isNew ? null : (story.data ?? null)} />;
}

type DialogKind = "publish" | "schedule" | "archive" | "delete";

function StoryEditor({ story }: { story: AdminStory | null }) {
  const canEdit = useCan("stories.edit");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const create = useCreateStory();
  const update = useUpdateStory();
  const publish = usePublishStory();
  const archive = useArchiveStory();
  const remove = useDeleteStory();

  const [category, setCategory] = useState<StoryCategory>(story?.category ?? "pattern");
  const [types, setTypes] = useState<string[]>(story?.puzzleTypes ?? []);
  const [cover, setCover] = useState<ImageRef | null>(story?.cover ?? null);
  const [text, setText] = useState<ContentState<TextKey>>(() => textState(TEXT_FIELDS, story?.content));
  const [slides, setSlides] = useState<EditSlide[]>(() => story?.slides.map(slideFromApi) ?? []);
  const [ctaAction, setCtaAction] = useState<CtaAction>(story?.ctaAction ?? "none");
  const [link, setLink] = useState<AppLink>(story?.link ?? "home");
  const [push, setPush] = useState(story?.push ?? true);
  const [locale, setLocale] = useState<Locale>("en");
  const [selected, setSelected] = useState(0);
  const [slidesTr, setSlidesTr] = useState<SlidesTranslation>({ from: null, model: null, costUsd: null });
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [scheduleAt, setScheduleAt] = useState(() => toUtcInput(story?.publishAt ?? new Date(Date.now() + 24 * 3_600_000).toISOString()));
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const draft = (list: EditSlide[] = slides): StoryDraft => ({
    category,
    puzzleTypes: types,
    coverImageId: cover?.id ?? null,
    content: toLocalized(text.draft, TEXT_FIELDS).content as StoryContent,
    slides: list.map(slideToApi),
    ctaAction,
    link: ctaAction === "link" ? link : null,
    push,
  });
  const current = JSON.stringify(draft());
  const [baseline, setBaseline] = useState(current);
  const dirty = current !== baseline;

  const status = story?.status ?? "draft";
  const live = status === "published" || status === "scheduled";
  const problems = storyProblems({ coverImageId: cover?.id ?? null, slides, enCta: text.draft.en.cta, ctaAction, link: ctaAction === "link" ? link : null });
  const saveProblem = !text.draft.en.title.trim()
    ? "Write the English title."
    : live && problems.length
      ? `A ${status} story must stay publishable: ${problems[0]}`
      : null;
  const enTitle = text.draft.en.title.trim();

  /** Saves the editor (create or update) and returns the story; adopts the slide ids the API gave. */
  const save = async (): Promise<AdminStory> => {
    const d = draft();
    if (!story) {
      const made = await create.mutateAsync(d);
      setBaseline(JSON.stringify(d));
      navigate(`/learn/${made.id}`, { replace: true });
      return made;
    }
    const saved = await update.mutateAsync({ id: story.id, patch: d });
    const withIds = slides.map((s, i) => ({ ...s, id: saved.slides[i]?.id ?? s.id }));
    setSlides(withIds);
    setBaseline(JSON.stringify(draft(withIds)));
    return saved;
  };

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    try {
      const done = await fn();
      if (done !== null) setNotice(done);
      setDialog(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const onSave = () =>
    void run(async () => {
      await save();
      return story ? "Saved." : null;
    });

  const onPublish = (at?: string) =>
    void run(async () => {
      const target = dirty || !story ? await save() : story;
      const r = await publish.mutateAsync({ id: target.id, ...(at ? { at } : {}) });
      if (r.status === "scheduled") return `Scheduled: goes live ${formatDateTime(r.publishAt!)}${r.push ? ", with the lesson push" : ""}.`;
      return r.push ? `Published · lesson push to ${fmtInt(r.stats.pushed)} ${r.stats.pushed === 1 ? "player" : "players"}.` : "Published · no push (turned off for this story).";
    });

  const scheduleIso = fromUtcInput(scheduleAt);
  const scheduleProblem = !scheduleIso ? "Pick a date and time." : Date.parse(scheduleIso) <= Date.now() + 60_000 ? "Pick a time in the future (UTC)." : null;

  const publishLabel = status === "archived" ? "Publish again…" : "Publish now…";
  const canPublish = problems.length === 0 && !saveProblem;
  const headActions = canEdit ? (
    <div className={l.headActions}>
      {story ? (
        <>
          {status === "draft" ? (
            <Button variant="ghost" onClick={() => setDialog("delete")} disabled={busy}>
              Delete…
            </Button>
          ) : null}
          {status !== "draft" && status !== "archived" ? (
            <Button variant="ghost" onClick={() => setDialog("archive")} disabled={busy}>
              Archive…
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onSave} disabled={!dirty || saveProblem !== null} busy={busy && dialog === null} data-testid="story-save">
            Save
          </Button>
          {status !== "published" ? (
            <>
              <Button variant="secondary" onClick={() => setDialog("schedule")} disabled={!canPublish || busy} title={canPublish ? undefined : "Fix what blocks publishing first"} data-testid="story-schedule">
                {status === "scheduled" ? "Reschedule…" : "Schedule…"}
              </Button>
              <Button variant="primary" onClick={() => setDialog("publish")} disabled={!canPublish || busy} title={canPublish ? undefined : "Fix what blocks publishing first"} data-testid="story-publish">
                {publishLabel}
              </Button>
            </>
          ) : null}
        </>
      ) : (
        <Button variant="primary" onClick={onSave} disabled={saveProblem !== null} busy={busy} data-testid="story-create">
          Create draft
        </Button>
      )}
    </div>
  ) : null;

  return (
    <>
      <PageHeader
        title={enTitle || (story ? "Untitled story" : "New story")}
        meta={
          <span className={l.statusLine}>
            <Link to="/learn" className={l.back}>
              ← Stories
            </Link>
            <Chip tone={STATUS_TONE[status]}>{story ? status : "new"}</Chip>
            {story?.status === "published" && story.publishedAt ? <span>published {formatDateTime(story.publishedAt)}</span> : null}
            {story?.status === "scheduled" && story.publishAt ? <span data-testid="scheduled-at">goes live {formatDateTime(story.publishAt)}</span> : null}
            {dirty ? <span className={m.required}>unsaved changes</span> : null}
            {!canEdit ? (
              <span className={styles.readOnly} data-testid="read-only">
                Read-only · editing needs the editor role (you are {admin?.role})
              </span>
            ) : null}
          </span>
        }
        actions={headActions}
      />
      {notice ? (
        <p className={l.savedNotice} data-testid="story-notice">
          {notice}
        </p>
      ) : null}
      {dialog === null ? <InlineError error={error} /> : null}

      <div className={l.editor}>
        <div className={l.form}>
          <fieldset className={l.fieldset} disabled={!canEdit || busy}>
            <Card title="Story">
              <div className={l.cardBody}>
                <div className={l.twoUp}>
                  <Field label="Category" hint={category === "general" ? "General: the button practises the first puzzle type's category." : "Its Learn section, and the Focus practice the button opens."}>
                    <Select value={category} onChange={(e) => setCategory(e.target.value as StoryCategory)} data-testid="story-category">
                      {STORY_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {CATEGORY_LABEL[c]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Button" hint={ctaAction === "practice" ? "Opens Focus practice for the category (or the first puzzle type's)." : ctaAction === "link" ? "Opens a place in the app." : "No button on the last slide."}>
                    <Select value={ctaAction} onChange={(e) => setCtaAction(e.target.value as CtaAction)} data-testid="story-cta">
                      {CTA_ACTIONS.map((a) => (
                        <option key={a} value={a}>
                          {CTA_LABEL[a]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                {ctaAction === "link" ? (
                  <Field label="Opens">
                    <Select value={link} onChange={(e) => setLink(e.target.value as AppLink)} className={l.linkSelect} data-testid="story-link">
                      {APP_LINKS.map((x) => (
                        <option key={x} value={x}>
                          {APP_LINK_LABEL[x]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
                <PuzzleTypesField value={types} onChange={setTypes} />
                <ImageField
                  image={cover}
                  onChange={setCover}
                  recommended={COVER_IMAGE}
                  hint="square, shown as a circle on Home; an illustration without text"
                  shape="square"
                  label="Cover · required to publish"
                  testId="cover"
                />
                <SwitchRow
                  title="Send a lesson push when published"
                  hint="To players with lessons on (Profile → Notifications → Lessons), in their language: the title, then the subtitle. Quiet hours apply; editing a published story sends no new push."
                  checked={push}
                  onChange={setPush}
                  label="Send a lesson push when published"
                  testId="story-push"
                />
              </div>
            </Card>

            <Card title="Text">
              <ContentEditor
                value={text}
                onChange={setText}
                fields={TEXT_SPECS}
                englishRequired={["title"]}
                locale={locale}
                onLocale={setLocale}
                needs={{ cta: ctaAction !== "none" ? "required for the button" : null }}
                note={<ClaimsNote>Describe how to practise and how reasoning works. Never promise IQ gains or health benefits.</ClaimsNote>}
              />
            </Card>

            <Card title={`Slides · ${slides.length} of ${MAX_SLIDES}`}>
              <SlidesEditor
                slides={slides}
                onChange={setSlides}
                locale={locale}
                onLocale={setLocale}
                selected={selected}
                onSelect={setSelected}
                translation={slidesTr}
                onTranslation={setSlidesTr}
              />
            </Card>
          </fieldset>
        </div>

        <aside className={l.side}>
          <StoryPreview
            title={(text.draft[locale].title || text.draft.en.title).trim()}
            slides={slides}
            index={selected}
            onIndex={setSelected}
            locale={locale}
            onLocale={setLocale}
            cta={(text.draft[locale].cta || text.draft.en.cta).trim()}
            ctaAction={ctaAction}
            cover={cover}
          />
          <Card title={problems.length ? "Before it can go out" : "Ready to publish"}>
            {problems.length ? (
              <ul className={l.checklist} data-testid="story-problems">
                {problems.map((p) => (
                  <li key={p} className={m.required}>
                    {p}
                  </li>
                ))}
              </ul>
            ) : (
              <p className={l.ready} data-testid="story-ready">
                Every check passes: a cover, slides with their English text, and the button.
              </p>
            )}
            {story && story.stats.views + story.stats.pushed > 0 ? (
              <dl className={l.stats} style={{ marginTop: 12 }} data-testid="story-stats">
                <div>
                  <dt>views</dt>
                  <dd>{fmtInt(story.stats.views)}</dd>
                </div>
                <div>
                  <dt>completed</dt>
                  <dd>{fmtInt(story.stats.completed)}</dd>
                </div>
                <div>
                  <dt>rate</dt>
                  <dd>{fmtPct(story.stats.completionRate)}</dd>
                </div>
                <div>
                  <dt>avg slide</dt>
                  <dd>{story.stats.avgSlide ?? "—"}</dd>
                </div>
                <div>
                  <dt>pushed</dt>
                  <dd>{fmtInt(story.stats.pushed)}</dd>
                </div>
              </dl>
            ) : null}
          </Card>
        </aside>
      </div>

      <ConfirmDialog
        open={dialog === "publish"}
        title={status === "scheduled" ? `Publish “${enTitle}” now instead?` : `Publish “${enTitle}” now?`}
        confirmLabel={dirty ? "Save and publish" : "Publish now"}
        busy={busy}
        error={error}
        onCancel={() => setDialog(null)}
        onConfirm={() => onPublish()}
      >
        <p className={styles.prose}>
          It goes live in every player's Learn screen.{" "}
          {push ? "A lesson push goes to players with lessons on, in their language; players in their quiet hours get it when the hours end." : "No push goes out (turned off for this story)."}
        </p>
        {dirty ? <p className={styles.footNote}>Your unsaved changes are saved first.</p> : null}
      </ConfirmDialog>

      <Dialog
        open={dialog === "schedule"}
        title={status === "scheduled" ? "Reschedule" : `Schedule “${enTitle}”`}
        onClose={() => setDialog(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialog(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" busy={busy} disabled={scheduleProblem !== null} onClick={() => scheduleIso && onPublish(scheduleIso)} data-testid="schedule-confirm">
              {dirty ? "Save and schedule" : "Schedule"}
            </Button>
          </>
        }
      >
        <Field label="Goes live at (UTC)" hint={scheduleProblem ?? (push ? "The lesson push goes out then." : "No push (turned off for this story).")}>
          <TextInput type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="schedule-at" />
        </Field>
        <InlineError error={error} />
      </Dialog>

      <ConfirmDialog
        open={dialog === "archive"}
        title={`Archive “${enTitle}”?`}
        confirmLabel="Archive"
        tone="danger"
        busy={busy}
        error={error}
        onCancel={() => setDialog(null)}
        onConfirm={() =>
          story &&
          void run(async () => {
            await archive.mutateAsync(story.id);
            return "Archived: hidden from players. Publish it again to bring it back.";
          })
        }
      >
        <p className={styles.prose}>Players stop seeing it at once{status === "scheduled" ? ", and it won't go live" : ""}. Its stats stay.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === "delete"}
        title={`Delete “${enTitle || "this draft"}”?`}
        confirmLabel="Delete draft"
        tone="danger"
        busy={busy}
        error={error}
        onCancel={() => setDialog(null)}
        onConfirm={() =>
          story &&
          void run(async () => {
            await remove.mutateAsync(story.id);
            navigate("/learn");
            return null;
          })
        }
      >
        <p className={styles.prose}>The draft and its slides are deleted. This can't be undone.</p>
      </ConfirmDialog>
    </>
  );
}
